/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef } from 'ag-grid-enterprise';
import {
  FIVE_DIGIT_WIDTH,
  SIX_DIGIT_WIDTH,
  TEXT_WIDTH,
  THREE_DIGIT_WIDTH,
  TWO_DIGIT_WIDTH,
  bestLapComparator,
  bestLapFormatter,
  bestTimeComparator,
  defaultCellStyle,
  deltaComparator,
  deltaValueFormatter,
  gapToReferenceValueFormatter,
  lsLapsFormatter,
  numberAsStringComparator,
  pitStatusCellRenderer,
  ppLapValueFormatter,
  rsLapsFormatter,
  threeDecimalValueFormatter,
  unverifiedCellStyle,
} from 'components/common/Tables/CommonGridFormatters';
import { LapDataWithReferenceObject } from 'jotai/lapData/LapDataObject';
import { PitStrategySource } from 'models/viewModels';
import LocationRenderer from './RaceOrderTableColumnRenderers';

export enum RaceOrderTableColId {
  RunningPosition = 'runningPosition',
  VehicleNumber = 'vehicleNumber',
  DriverFullName = 'driverFullName',
  LapsCompleted = 'lapsCompleted',
  Delta = 'deltaToTarget',
  LapsLed = 'lapsLed',
  LastLapTime = 'lastLapTime',
  BestLap = 'bestLap',
  BestLapTime = 'bestLapTime',
  MovingAverageLapTime = 'movingAverageLapTime',
  Group = 'runName',
  PPLap = 'ppLap',
  PPStrat = 'ppStrat',
  LeftSideLaps = 'tireAgeMetadata.lsLaps',
  RightSideLaps = 'tireAgeMetadata.rsLaps',
  GapToReference = 'gapToReference',
  LastPit = 'lastPit',
  LapsSincePit = 'lapsSincePit',
  TireType = 'tireType',
  Overtake = 'overtakeRemaining',
  PitStatus = 'pitStatus',
  Location = 'loopName',
}

const ppStratCellStyle = (data: LapDataWithReferenceObject) => {
  return data.ppStrat && data.source && data.source.toString() !== PitStrategySource.PITRHO.toString()
    ? unverifiedCellStyle
    : defaultCellStyle;
};

export const raceOrderTableColDefs: ColDef[] = [
  {
    colId: RaceOrderTableColId.RunningPosition,
    headerName: 'Pos',
    headerTooltip: 'Race Position',
    sort: 'asc',
    type: 'numericColumn',
    initialWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.VehicleNumber,
    cellClass: 'stringType',
    cellStyle: defaultCellStyle,
    headerName: 'Vehicle',
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
    initialWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.DriverFullName,
    headerName: 'Driver',
    headerTooltip: 'Driver Name',
    type: 'rightAligned',
    initialWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.LapsCompleted,
    headerName: 'Laps',
    headerTooltip: 'Laps Completed',
    type: 'numericColumn',
    initialWidth: THREE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.LastLapTime,
    headerName: 'LastLT',
    type: 'numericColumn',
    flex: 1,
    headerTooltip: 'Last Lap Time',
    valueFormatter: threeDecimalValueFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.Delta,
    headerName: 'Gap',
    headerTooltip: 'Gap to Leader',
    type: 'numericColumn',
    valueFormatter: deltaValueFormatter,
    comparator: deltaComparator,
    initialWidth: 100,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.BestLap,
    headerName: 'BestLN',
    headerTooltip: 'Best Lap Number',
    type: 'numericColumn',
    valueFormatter: bestLapFormatter,
    comparator: bestLapComparator,
    initialWidth: THREE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.BestLapTime,
    headerName: 'BestLT',
    type: 'numericColumn',
    headerTooltip: 'Best Lap Time',
    valueFormatter: threeDecimalValueFormatter,
    comparator: bestTimeComparator,
    initialWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.LapsLed,
    headerName: 'LL',
    headerTooltip: 'Laps Led',
    type: 'numericColumn',
    initialWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.MovingAverageLapTime,
    cellClass: 'numberType',
    cellStyle: defaultCellStyle,
    headerName: 'MovLT',
    type: 'numericColumn',
    headerTooltip: 'Moving Average Lap Time',
    valueFormatter: threeDecimalValueFormatter,
    initialWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.GapToReference,
    headerName: 'Gap Ref',
    headerTooltip: 'Gap to Reference Vehicle',
    type: 'numericColumn',
    valueFormatter: gapToReferenceValueFormatter,
    width: FIVE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
    sortable: false,
  },
];

export const nascarRaceOrderTableColDefs: ColDef[] = [
  ...raceOrderTableColDefs,
  {
    colId: RaceOrderTableColId.Group,
    headerName: 'Group',
    headerTooltip: 'Run Group',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    initialWidth: 50,
    suppressHeaderMenuButton: false,
    type: 'rightAligned',
  },
  {
    colId: RaceOrderTableColId.PPLap,
    headerName: 'ppLap',
    headerTooltip: 'Last time this driver took a pit stop',
    type: 'numericColumn',
    initialWidth: TWO_DIGIT_WIDTH,
    valueFormatter: ppLapValueFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.PPStrat,
    headerName: 'ppStrat',
    headerTooltip: 'What the driver took at their last pit stop (4T, 2L, 2R, FO)',
    flex: 1,
    cellStyle: (cell) => ppStratCellStyle(cell.data),
    headerClass: 'ag-right-aligned-header',
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.LeftSideLaps,
    headerName: 'LSLaps',
    headerTooltip: 'Laps on left side tires',
    type: 'numericColumn',
    initialWidth: TEXT_WIDTH,
    valueFormatter: lsLapsFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.RightSideLaps,
    headerName: 'RSLaps',
    headerTooltip: 'Laps on right side tires',
    type: 'numericColumn',
    initialWidth: TEXT_WIDTH,
    valueFormatter: rsLapsFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.Location,
    cellStyle: {
      textAlign: 'center',
    },
    headerName: 'Location',
    headerTooltip: 'Location',
    type: 'rightAligned',
    cellDataType: 'text',
    initialWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
    cellRenderer: LocationRenderer,
  },
];

export const indyRaceOrderTableColDefs: ColDef[] = [
  ...raceOrderTableColDefs,
  {
    colId: RaceOrderTableColId.LastPit,
    headerName: 'LP',
    headerTooltip: 'Last pit lap number',
    type: 'numericColumn',
    initialWidth: FIVE_DIGIT_WIDTH,
    width: THREE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.LapsSincePit,
    headerName: 'LSP',
    headerTooltip: 'Laps since last pit stop',
    type: 'numericColumn',
    initialWidth: THREE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.TireType,
    headerName: 'Tire',
    headerTooltip: 'Tire Type',
    type: 'rightAligned',
    initialWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.Overtake,
    headerName: 'OT',
    headerTooltip: 'Overtake Available',
    type: 'numericColumn',
    initialWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: RaceOrderTableColId.PitStatus,
    headerName: 'PStat',
    headerTooltip: 'Pit Status',
    type: 'rightAligned',
    cellDataType: 'text',
    initialWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
    cellRenderer: pitStatusCellRenderer,
  },
];
